import React, { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntryConfigUnion } from "@/types/entryConfig";
import { Archive, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { TraitUsage } from "@/services/firebase";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import AddTraitForm from "./AddTraitForm";

interface EditTraitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trait: EntryConfigUnion | null;
  onTraitUpdated: (config: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">) => void;
  onTraitDeleted: (traitId: string) => Promise<void>;
  onTraitArchived: (traitId: string) => Promise<void>;
  onCheckTraitUsage: (traitId: string) => Promise<{ isUsed: boolean; trialNames?: string[]; usages?: TraitUsage[] }>;
  canDelete?: boolean;
  existingNames?: string[];
}

const EditTraitDialog: React.FC<EditTraitDialogProps> = ({
  open,
  onOpenChange,
  trait,
  onTraitUpdated,
  onTraitDeleted,
  onTraitArchived,
  onCheckTraitUsage,
  canDelete = false,
  existingNames,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [usageDetails, setUsageDetails] = useState<{ usages: TraitUsage[] }>({ usages: [] });
  const [isDirty, setIsDirty] = useState(false);
  // Warn before discarding in-progress trait edits (Escape, click-outside, X, Cancel).
  const guard = useUnsavedChangesGuard({ isDirty, onOpenChange });
  // The usage links open in a new tab, so they must name this org or the tab opens in the last-used one.
  const paths = useOrgPaths();

  const handleTraitUpdated = (config: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">) => {
    // Prevent updating system traits
    if (trait?.isSystem) {
      return;
    }

    onTraitUpdated(config);
    onOpenChange(false);
  };

  const handleDeleteClick = async () => {
    if (!trait?.id) return;

    // Used traits can only be archived (preserves collected data); unused traits
    // offer a permanent delete.
    const usage = await onCheckTraitUsage(trait.id);
    if (usage.isUsed) {
      setUsageDetails({ usages: usage.usages || [] });
      setShowArchiveConfirm(true);
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!trait?.id) return;
    // Don't manually close either dialog here. Radix's AlertDialogAction
    // auto-closes the AlertDialog, and the parent closes the outer Dialog
    // after the delete resolves. Closing both in the same synchronous tick
    // strands pointer-events: none on document.body and freezes the page.
    await onTraitDeleted(trait.id);
  };

  const handleConfirmArchive = async () => {
    if (!trait?.id) return;
    // Same close discipline as delete: let Radix auto-close the AlertDialog and
    // let the parent close the outer Dialog after the archive resolves.
    await onTraitArchived(trait.id);
  };

  if (!trait) return null;

  const MAX_VISIBLE_USAGES = 5;
  const archiveTrialCount = new Set(usageDetails.usages.map((u) => u.trialId)).size;
  const visibleUsages = usageDetails.usages.slice(0, MAX_VISIBLE_USAGES);
  const hiddenUsageCount = usageDetails.usages.length - visibleUsages.length;

  return (
    <>
      <Dialog open={open} onOpenChange={guard.guardedOnOpenChange}>
        <DialogContent className="w-[min(96vw,1180px)] max-w-none max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Trait</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-4 -mr-2">
            <AddTraitForm
              initialTrait={trait}
              onSubmit={handleTraitUpdated}
              onCancel={guard.requestClose}
              onDelete={handleDeleteClick}
              showDelete={canDelete}
              existingNames={existingNames}
              onDirtyChange={setIsDirty}
            />
          </div>
        </DialogContent>
      </Dialog>

      {guard.confirmation}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trait</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{trait?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Dialog (used traits cannot be hard-deleted) */}
      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Trait</AlertDialogTitle>
            <AlertDialogDescription>
              "{trait?.name}" is used in {archiveTrialCount} trial
              {archiveTrialCount === 1 ? "" : "s"}, so it can't be deleted. It will be archived and
              removed from the trait pickers for new tasks. Existing data stays visible and
              exportable, and existing tasks keep collecting. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {visibleUsages.length > 0 && (
            <div className="mt-1">
              <p className="text-sm font-medium text-foreground">Used in:</p>
              <ul className="mt-1 space-y-1 text-sm">
                {visibleUsages.map((u) => (
                  <li key={`${u.trialId}/${u.experimentId}/${u.taskId}`}>
                    <Link
                      to={paths.task(u.trialId, u.experimentId, u.taskId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:text-primary/80">
                      <span className="underline underline-offset-2">
                        {u.trialName} — {u.taskName}
                      </span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
                {hiddenUsageCount > 0 && (
                  <li className="text-muted-foreground">+ {hiddenUsageCount} more</li>
                )}
              </ul>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditTraitDialog;
